import React from "react";
import { Button } from "../../../../ui/components/Button";
import { Avatar } from "../../../../ui/components/Avatar";
import {
  FeatherAlertCircle,
  FeatherAlertTriangle,
  FeatherInfo,
  FeatherCheck,
  FeatherClock,
} from "@subframe/core";
import { ApprovalEntity } from "../../domain/entities/ApprovalEntity";
import { formatJournalChangesForBanner } from "../../../../shared/presentation/components/ReviewRequestModal";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { getBankAccountApprovalTitle } from "../../../../shared/utils/bankAccountApprovalUi";
import { formatInvoiceBillChangeSummaryLines } from "../../../../shared/utils/review";
import {
  formatCombinedActionTypes,
} from "../../../../shared/utils/review/reviewActionLabels";
import { getStoredAccountingBackend } from "../../../../config/accounting";
import { useTaxes } from "../../../../shared/presentation/hooks/useTaxes";
import { formatDateTimeInIST } from "../../../../shared/utils/date";
import { InvoiceBillApprovalCardSummary } from "./InvoiceBillApprovalCardSummary";

interface ApprovalCardProps {
  approval: ApprovalEntity;
  onReview: () => void;
  id?: string;
}

const ENTITY_TYPE_DISPLAY: Record<string, string> = {
  invoice: "Invoice",
  bill: "Bill",
  customer: "Customer",
  vendor: "Vendor",
  credit_note: "Credit Note",
  debit_note: "Debit Note",
  bank_transaction: "Bank Transaction",
  bank_account: "Bank Account",
  journal: "Journal",
  ap_ar_journal: "AP/AR Journal",
  journal_template: "Recurring Journal",
  gl: "General Ledger",
  month_end_checklist_item: "Month End Checklist",
  tenant_compliance_calendar: "Compliance",
};

const getStatusBadgeVariant = (
  status: string,
  actionTypes?: string[],
): "warning" | "error" | "brand" => {
  if (actionTypes?.includes("create") || actionTypes?.includes("new")) {
    return "warning";
  }
  if (actionTypes?.includes("update") || actionTypes?.includes("revised")) {
    return "error";
  }
  if (actionTypes?.includes("auto_flagged")) {
    return "brand";
  }
  return "warning";
};

const getStatusBadgeText = (actionTypes?: string[]): string => {
  if (actionTypes?.includes("create") || actionTypes?.includes("new")) {
    return "New";
  }
  if (actionTypes?.includes("update") || actionTypes?.includes("revised")) {
    return "Revised";
  }
  if (actionTypes?.includes("auto_flagged")) {
    return "Auto-flagged";
  }
  return "New";
};

const getBorderColor = (status: string, actionTypes?: string[]): string => {
  if (actionTypes?.includes("create") || actionTypes?.includes("new")) {
    return "border-l-warning-600";
  }
  if (actionTypes?.includes("update") || actionTypes?.includes("revised")) {
    return "border-l-error-600";
  }
  if (actionTypes?.includes("auto_flagged")) {
    return "border-l-brand-600";
  }
  return "border-l-warning-600";
};

const getAlertIcon = (actionTypes?: string[]) => {
  if (actionTypes?.includes("update") || actionTypes?.includes("revised")) {
    return FeatherAlertTriangle;
  }
  if (actionTypes?.includes("auto_flagged")) {
    return FeatherInfo;
  }
  return FeatherAlertCircle;
};

const formatAmount = (
  rawJson: any,
  entityType?: string,
  changeDetails?: any,
): string => {
  if (!rawJson) return "N/A";

  // Customer and vendor entities don't have amounts
  if (entityType === "customer" || entityType === "vendor") {
    return "";
  }

  // For credit notes, use original_credit_note.credit_note.json.total if available
  if (entityType === "credit_note" && changeDetails?.original_credit_note) {
    const originalCreditNote = changeDetails.original_credit_note.credit_note;
    if (originalCreditNote?.json) {
      const creditNoteJson =
        typeof originalCreditNote.json === "string"
          ? JSON.parse(originalCreditNote.json)
          : originalCreditNote.json;
      const amount = creditNoteJson.total || 0;
      const currencySymbol = creditNoteJson.currency_symbol || "₹";
      return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  }

  // For bank transactions, use categorize_in_zoho_json if available
  if (entityType === "bank_transaction" && rawJson.categorize_in_zoho_json) {
    const categorizeData = rawJson.categorize_in_zoho_json;
    const amount =
      categorizeData.amount ||
      rawJson.transaction_amount ||
      rawJson.amount ||
      0;
    const currencySymbol =
      categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
    if (amount === 0) return "N/A";
    return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // For bank transactions without categorize_in_zoho_json, use transaction_amount
  if (entityType === "bank_transaction") {
    const amount = rawJson.transaction_amount || rawJson.amount || 0;
    const currencySymbol = rawJson.currency_symbol || "₹";
    if (amount === 0) return "N/A";
    return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // Try to get amount from different possible fields
  const amount =
    rawJson.total ||
    rawJson.amount ||
    rawJson.sub_total ||
    rawJson.total_amount ||
    0;

  const currencySymbol = rawJson.currency_symbol || "USD";

  return `${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatDateFull = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    // Try to parse the date
    const date = new Date(dateString);

    // Format as DD-MM-YYYY
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return dateString;
  }
};

const getReferenceNumber = (rawJson: any, entityType: string): string => {
  if (!rawJson) return "N/A";

  switch (entityType) {
    case "invoice":
      return rawJson.invoice_number || "N/A";
    case "bill":
      return rawJson.bill_number || rawJson.vendor_bill_number || "N/A";
    case "customer":
      return rawJson.customer_name || rawJson.customerid || "N/A";
    case "vendor":
      return (
        rawJson.vendor_legal_name ||
        rawJson.vendor_name ||
        rawJson.vendorid ||
        "N/A"
      );
    case "credit_note":
      return (
        rawJson.creditnote_number ||
        rawJson.credit_note_number ||
        rawJson.reference_number ||
        "N/A"
      );
    case "debit_note":
      return (
        rawJson.debitnote_number ||
        rawJson.debit_note_number ||
        rawJson.reference_number ||
        "N/A"
      );
    case "journal":
    case "ap_ar_journal":
      return rawJson.journal_number || rawJson.reference_number || "N/A";
    case "bank_transaction":
      return rawJson.transaction_id || rawJson.reference_number || "N/A";
    default:
      return "N/A";
  }
};

/**
 * Calculate total debit and credit from journal line items
 */
const calculateJournalTotals = (
  lineItems: any[],
): {
  totalDebit: number;
  totalCredit: number;
} => {
  let totalDebit = 0;
  let totalCredit = 0;

  if (!lineItems || !Array.isArray(lineItems)) {
    return { totalDebit: 0, totalCredit: 0 };
  }

  lineItems.forEach((item) => {
    const amount = parseFloat(item.amount || item.debit || item.credit || 0);
    const isDebitOrCredit = item.is_debit_or_credit?.toLowerCase();

    if (isDebitOrCredit === "debit") {
      totalDebit += amount;
    } else if (isDebitOrCredit === "credit") {
      totalCredit += amount;
    } else {
      // Fallback: if no is_debit_or_credit, check debit/credit fields
      if (item.debit && parseFloat(item.debit) > 0) {
        totalDebit += parseFloat(item.debit);
      }
      if (item.credit && parseFloat(item.credit) > 0) {
        totalCredit += parseFloat(item.credit);
      }
    }
  });

  return { totalDebit, totalCredit };
};

/**
 * Format journal information for display
 */
const formatJournalInfo = (
  originalJson: any,
): {
  journalNumber: string;
  currencySymbol: string;
  totalDebit: string;
  totalCredit: string;
  notes: string;
} => {
  if (!originalJson) {
    return {
      journalNumber: "N/A",
      currencySymbol: "₹",
      totalDebit: "0.00",
      totalCredit: "0.00",
      notes: "N/A",
    };
  }

  const journalNumber =
    originalJson.journal_number || originalJson.reference_number || "N/A";
  const currencySymbol = originalJson.currency_symbol || "₹";
  const notes = originalJson.notes || "N/A";

  const lineItems = originalJson.line_items || [];
  const { totalDebit, totalCredit } = calculateJournalTotals(lineItems);

  return {
    journalNumber,
    currencySymbol,
    totalDebit: totalDebit.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    totalCredit: totalCredit.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    notes,
  };
};

/**
 * Extract journal details from field_changes and original_json for card display
 * Returns only non-null values (no "N/A")
 * Prioritizes original_json over raw_json for amounts and journal number
 */
const getJournalDetailsForCard = (
  changeDetails: any,
): {
  date: string | null;
  notes: string | null;
  referenceNumber: string | null;
  /** ZOUR-65 F4: the GST registration this journal is filed under. NULL = primary. */
  locationId: string | null;
  currencySymbol: string;
  totalDebit: string;
  totalCredit: string;
} => {
  const originalJson = changeDetails?.original_json;
  const rawJson = changeDetails?.raw_json;
  const fieldChanges = changeDetails?.field_changes || [];

  // Extract from field_changes first, then fallback to original_json, then raw_json
  let date: string | null = null;
  let notes: string | null = null;
  let referenceNumber: string | null = null;

  // Extract from field_changes
  fieldChanges.forEach((fc: any) => {
    const fieldLower = fc.field.toLowerCase();
    if (
      fieldLower === "date" &&
      fc.new &&
      fc.new !== "N/A" &&
      fc.new !== "(empty)"
    ) {
      date = fc.new;
    } else if (
      (fieldLower === "notes" || fieldLower === "note") &&
      fc.new &&
      fc.new !== "N/A" &&
      fc.new !== "(empty)"
    ) {
      notes = fc.new;
    } else if (
      (fieldLower === "reference_number" ||
        fieldLower === "reference number" ||
        fieldLower === "journal_number" ||
        fieldLower === "journal number") &&
      fc.new &&
      fc.new !== "N/A" &&
      fc.new !== "(empty)"
    ) {
      referenceNumber = fc.new;
    }
  });

  // Fallback to original_json first, then raw_json if not found in field_changes
  if (!date) {
    date = originalJson?.date || rawJson?.date || null;
  }
  if (!notes) {
    notes = originalJson?.notes || rawJson?.notes || null;
  }
  if (!referenceNumber) {
    referenceNumber =
      originalJson?.journal_number ||
      originalJson?.reference_number ||
      rawJson?.journal_number ||
      rawJson?.reference_number ||
      null;
  }

  // Calculate totals from original_json first, then fallback to raw_json
  const lineItems = originalJson?.line_items || rawJson?.line_items || [];
  const { totalDebit, totalCredit } = calculateJournalTotals(lineItems);
  const currencySymbol =
    originalJson?.currency_symbol ||
    rawJson?.currency_symbol ||
    rawJson?.journal?.currency_symbol ||
    "₹";

  // ZOUR-65 F4: `??` and not `||`, because NULL is a real answer here — it
  // means the primary location — and must not fall through to another source.
  const locationId =
    originalJson?.location_id ??
    rawJson?.location_id ??
    rawJson?.journal?.location_id ??
    null;

  return {
    date,
    notes,
    referenceNumber,
    locationId,
    currencySymbol,
    totalDebit: totalDebit.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    totalCredit: totalCredit.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  };
};

const getCustomerOrVendorName = (rawJson: any, entityType: string): string => {
  if (!rawJson) return "N/A";

  if (entityType === "customer") {
    return rawJson.customer_name || "N/A";
  }
  if (entityType === "vendor") {
    return rawJson.vendor_legal_name || rawJson.vendor_name || "N/A";
  }
  if (entityType === "invoice" || entityType === "credit_note") {
    return rawJson.customer_name || "N/A";
  }
  if (entityType === "bill" || entityType === "debit_note") {
    return rawJson.vendor_name || "N/A";
  }
  if (entityType === "bank_transaction") {
    if (rawJson.category_type === "customer_payment") {
      return rawJson.customer_name || "N/A";
    }
    if (rawJson.category_type === "vendor_payment") {
      return rawJson.vendor_name || "N/A";
    }
  }
  return "N/A";
};

// Helper: Convert dates from YYYY-MM-DD to DD-MM-YYYY
const convertDateFormat = (dateStr: string): string => {
  // Check if it's a date in YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (dateRegex.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }
  return dateStr;
};

// Helper: Format snake_case values to readable format
const formatValue = (val: string): string => {
  if (!val || val === "empty") return val;
  const strVal = String(val);

  // Check if it's a date and convert format
  const convertedDate = convertDateFormat(strVal);
  if (convertedDate !== strVal) {
    return convertedDate;
  }

  if (
    strVal.includes("_") &&
    !strVal.startsWith("[") &&
    !strVal.startsWith("{")
  ) {
    return strVal
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  return strVal;
};

/**
 * Format action types into readable text (Post-close change first).
 */
const formatActionTypes = (actionTypes?: string[]): string | null => {
  if (!actionTypes || actionTypes.length === 0) return null;

  const backend = getStoredAccountingBackend();
  const extraLabels: Record<string, string> = {
    override_gl_permanent: "GL Override",
    edit: "Edit",
    approve: "Approve",
    advance: "Advance",
    unmatch: "Unmatch",
    create: "Create",
    split_transaction: "Split Transaction",
    split_transaction_amount: "Split Transaction Amount",
    new: "New",
    revised: "Revised",
    auto_flagged: "Auto-flagged",
    create_recurring: "Create Recurring",
    cancel_recurring_before_end: "Delete Before End",
    reject: "REJECT",
    duplicate: "Mark as Duplicate",
    write_off: "Write off",
    cancel_write_off: "Cancel write off",
    month_close: "Post-close change",
    ap_ar_create: "AP/AR Journal – Create",
    ap_ar_delete: "AP/AR Journal – Delete",
    bulk_upload: "Bulk Upload",
  };

  return formatCombinedActionTypes(actionTypes, backend, extraLabels);
};

/**
 * Format field changes for Credit/Debit Notes
 */
const formatCreditDebitNoteChanges = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
): string[] => {
  const changes: string[] = [];
  const idFieldsToHide = [
    "customer_id",
    "vendor_id",
    "account_id",
    "tax_id",
    "invoice_id",
    "bill_id",
    "_id",
    "id",
  ];

  const lineItemChanges: Record<
    number,
    {
      quantity?: { old: string; new: string };
      rate?: { old: string; new: string };
    }
  > = {};

  fieldChanges.forEach((fc) => {
    const fieldLower = fc.field.toLowerCase();
    const formattedOld = formatValue(fc.old);
    const formattedNew = formatValue(fc.new);

    if (formattedOld === formattedNew) return;

    if (
      idFieldsToHide.some((idField) => fieldLower.includes(idField)) ||
      fieldLower.endsWith("_id") ||
      fieldLower.endsWith("id")
    ) {
      return;
    }

    const lineItemMatch = fc.field.match(
      /line_items\[(\d+)\]\.(quantity|rate)/i,
    );
    if (lineItemMatch) {
      const index = parseInt(lineItemMatch[1], 10);
      const field = lineItemMatch[2].toLowerCase() as "quantity" | "rate";

      if (!lineItemChanges[index]) {
        lineItemChanges[index] = {};
      }
      lineItemChanges[index][field] = {
        old: formattedOld,
        new: formattedNew,
      };
      return;
    }

    if (!fc.field.startsWith("line_items") && !fc.field.includes("_")) {
      changes.push(`${fc.field}: ${formattedOld} → ${formattedNew}`);
    }
  });

  Object.keys(lineItemChanges)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .forEach((indexStr) => {
      const index = parseInt(indexStr, 10);
      const itemChanges = lineItemChanges[index];
      const lineNumber = index + 1;

      if (itemChanges.quantity && itemChanges.rate) {
        changes.push(
          `Line Item ${lineNumber}: Quantity ${itemChanges.quantity.old} → ${itemChanges.quantity.new}, Rate ${itemChanges.rate.old} → ${itemChanges.rate.new}`,
        );
      } else if (itemChanges.quantity) {
        changes.push(
          `Line Item ${lineNumber}: Quantity ${itemChanges.quantity.old} → ${itemChanges.quantity.new}`,
        );
      } else if (itemChanges.rate) {
        changes.push(
          `Line Item ${lineNumber}: Rate ${itemChanges.rate.old} → ${itemChanges.rate.new}`,
        );
      }
    });

  return changes;
};

/**
 * Format bank account / credit card creation details for display
 */
const formatBankAccountChangesForCard = (
  changeDetails: any,
  actionCode?: string,
): string[] => {
  const changes: string[] = [];
  if (!changeDetails?.raw_json) return changes;
  const rawJson = changeDetails.raw_json;
  const ac = (actionCode || "create").toLowerCase();
  const accountType =
    rawJson.account_type === "credit" || rawJson.account_type === "credit_card"
      ? "Credit card"
      : "Bank account";

  if (ac === "delete") {
    changes.push(`Delete ${accountType}: ${rawJson.account_name ?? "—"}`);
    return changes;
  }

  const currencyDisplay =
    [rawJson.currency_code, rawJson.currency_name]
      .filter(Boolean)
      .join(" – ") ||
    rawJson.currency_id ||
    "—";
  if (ac === "update") {
    changes.push(`Update ${accountType}: ${rawJson.account_name ?? "—"}`);
  } else {
    changes.push(`${accountType}: ${rawJson.account_name ?? "—"}`);
  }
  if (rawJson.account_number != null && rawJson.account_number !== "") {
    changes.push(`Account number: ${String(rawJson.account_number)}`);
  }
  changes.push(`Currency: ${currencyDisplay}`);
  const ob = rawJson.opening_balance;
  if (ob != null && ob !== "" && !Number.isNaN(Number(ob))) {
    changes.push(
      `Opening balance: ${Number(ob).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    );
  }
  if (
    rawJson.opening_balance_date != null &&
    String(rawJson.opening_balance_date).trim() !== ""
  ) {
    changes.push(
      `Opening balance date: ${String(rawJson.opening_balance_date)}`,
    );
  }
  return changes;
};

/**
 * Format field changes for Bank Transactions
 */
const formatBankTransactionChanges = (changeDetails: any): string[] => {
  const changes: string[] = [];
  if (!changeDetails?.raw_json) return changes;

  const rawJson = changeDetails.raw_json;
  const categoryType = rawJson.category_type || "";
  const categorizeData = rawJson.categorize_in_zoho_json;

  if (Array.isArray(rawJson.split_items)) {
    changes.push(
      `Split Transaction Amount: ${rawJson.split_items.length} item${rawJson.split_items.length === 1 ? "" : "s"}`,
    );

    rawJson.split_items.slice(0, 2).forEach((item: any, index: number) => {
      changes.push(
        `Item ${index + 1}: ${item.description || "N/A"} - ${item.amount ?? "N/A"}`,
      );
    });

    if (rawJson.split_items.length > 2) {
      changes.push(`+${rawJson.split_items.length - 2} more item${rawJson.split_items.length - 2 === 1 ? "" : "s"}`);
    }

    return changes;
  }

  if (categoryType) {
    changes.push(`Category Type: ${formatValue(categoryType)}`);
  }

  // Handle customer_payment category
  if (categoryType === "customer_payment") {
    if (rawJson.customer_name) {
      changes.push(`Customer: ${rawJson.customer_name}`);
    }

    if (categorizeData) {
      const invoices = categorizeData.invoices || [];
      const currencySymbol =
        categorizeData.currency_symbol || rawJson.currency_symbol || "₹";

      if (invoices.length > 0) {
        changes.push(
          `${invoices.length} invoice${invoices.length > 1 ? "s" : ""}`,
        );
        // Show total and balance for each invoice
        invoices.forEach((invoice: any, index: number) => {
          const invoiceNumber =
            invoice.invoice_number ||
            invoice.invoice_id ||
            `Invoice ${index + 1}`;
          const total = invoice.total || 0;
          const balance = invoice.balance || 0;
          const amountApplied = invoice.amount_applied || 0;

          changes.push(
            `${invoiceNumber}: Total ${currencySymbol} ${Number(
              total,
            ).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}, Balance ${currencySymbol} ${Number(balance).toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}${
              amountApplied > 0
                ? `, Applied ${currencySymbol} ${Number(
                    amountApplied,
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : ""
            }`,
          );
        });
      }

      if (rawJson.advance_payment && rawJson.advance_payment > 0) {
        changes.push(
          `Advance: ${currencySymbol} ${Number(
            rawJson.advance_payment,
          ).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        );
      }
    }
  }
  // Handle vendor_payment category
  else if (categoryType === "vendor_payment") {
    if (rawJson.vendor_name) {
      changes.push(`Vendor: ${rawJson.vendor_name}`);
    }

    if (categorizeData) {
      const bills = categorizeData.bills || [];
      const currencySymbol =
        categorizeData.currency_symbol || rawJson.currency_symbol || "₹";

      if (bills.length > 0) {
        changes.push(`${bills.length} bill${bills.length > 1 ? "s" : ""}`);
        // Show total and balance for each bill
        bills.forEach((bill: any, index: number) => {
          const billNumber =
            bill.bill_number || bill.bill_id || `Bill ${index + 1}`;
          const total = bill.total || 0;
          const balance = bill.balance || 0;
          const amountApplied = bill.amount_applied || 0;

          changes.push(
            `${billNumber}: Total ${currencySymbol} ${Number(
              total,
            ).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}, Balance ${currencySymbol} ${Number(balance).toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}${
              amountApplied > 0
                ? `, Applied ${currencySymbol} ${Number(
                    amountApplied,
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : ""
            }`,
          );
        });
      }

      if (rawJson.advance_payment && rawJson.advance_payment > 0) {
        changes.push(
          `Advance: ${currencySymbol} ${Number(
            rawJson.advance_payment,
          ).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        );
      }
    }
  }
  // Handle other category types
  else {
    if (rawJson.customer_name) {
      changes.push(`Customer: ${rawJson.customer_name}`);
    }
    if (rawJson.vendor_name) {
      changes.push(`Vendor: ${rawJson.vendor_name}`);
    }

    if (categorizeData) {
      const amount = categorizeData.amount || rawJson.amount || 0;
      const currencySymbol =
        categorizeData.currency_symbol || rawJson.currency_symbol || "₹";
      if (amount > 0) {
        changes.push(
          `Amount: ${currencySymbol} ${Number(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        );
      }
    }
  }

  return changes;
};

/**
 * Format journal line items from raw_json for create_recurring actions
 * Shows Account, Contact (if exists), and Debit/Credit for each line item
 */
const formatJournalLineItemsFromRawJson = (rawJson?: any): string[] => {
  if (!rawJson || !rawJson.line_items || !Array.isArray(rawJson.line_items)) {
    return [];
  }

  const changes: string[] = [];
  const currencySymbol = rawJson.currency_symbol || "₹";

  rawJson.line_items.forEach((item: any, index: number) => {
    const lineNumber = index + 1;
    const accountName = item.account_name || "N/A";
    const contactName = item.contact_name || null;
    const amount = parseFloat(item.amount || 0);
    const isDebitOrCredit = item.is_debit_or_credit?.toLowerCase();

    changes.push(`Line Item ${lineNumber}:`);
    changes.push(`  Account: ${accountName}`);

    if (contactName) {
      changes.push(`  Contact: ${contactName}`);
    }

    if (item.reference_display) {
      changes.push(`  Invoice / Bill: ${item.reference_display}`);
    }

    if (isDebitOrCredit === "debit" && amount > 0) {
      changes.push(
        `  Debit: ${currencySymbol} ${amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      );
    } else if (isDebitOrCredit === "credit" && amount > 0) {
      changes.push(
        `  Credit: ${currencySymbol} ${amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      );
    }

    changes.push("");
  });

  return changes;
};

/**
 * Format field changes for Journals
 * Uses the exported formatter from ReviewRequestModal
 * For create_recurring actions, uses raw_json to show all line items
 */
const formatJournalChanges = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
  rawJson?: any,
  originalJson?: any,
  actionTypes?: string[],
): string[] => {
  // For create_recurring action, show all line items from raw_json
  if (actionTypes?.includes("create_recurring")) {
    return formatJournalLineItemsFromRawJson(rawJson);
  }

  // For other actions, use the standard formatter
  return formatJournalChangesForBanner(fieldChanges, rawJson, originalJson);
};

// ============================================================================
// MAIN FORMATTER - Routes to module-specific formatter
// ============================================================================

const getChangeMessage = (
  changeDetails: any,
  entityType: string,
  actionCode: string | undefined,
  taxes: ReturnType<typeof useTaxes>["taxes"],
): string | null => {
  if (!changeDetails) return null;

  const ac = (actionCode || "").toLowerCase();
  if (
    (entityType === "customer" ||
      entityType === "vendor" ||
      entityType === "gl") &&
    ac === "bulk_upload" &&
    changeDetails.raw_json?.rows &&
    Array.isArray(changeDetails.raw_json.rows)
  ) {
    const n = changeDetails.raw_json.rows.length;
    return n > 0 ? `Bulk upload: ${n} row(s)` : "Bulk upload";
  }

  // Don't show change message for delete_after_push_to_zoho actions
  if (changeDetails.action_types?.includes("delete_after_push_to_zoho")) {
    return null;
  }

  if (entityType === "bank_account" && changeDetails.raw_json) {
    const formattedChanges = formatBankAccountChangesForCard(
      changeDetails,
      actionCode,
    );
    if (formattedChanges.length === 0) {
      return null;
    }
    if (formattedChanges.length <= 2) {
      return formattedChanges.join(", ");
    }
    return `${formattedChanges.slice(0, 2).join(", ")} +${
      formattedChanges.length - 2
    } more`;
  }

  // For journal with create_recurring action, show line items even if no field_changes
  if (
    entityType === "journal" &&
    changeDetails.action_types?.includes("create_recurring")
  ) {
    const formattedChanges = formatJournalLineItemsFromRawJson(
      changeDetails.raw_json,
    );
    if (formattedChanges.length === 0) {
      return null;
    }
    if (formattedChanges.length <= 2) {
      return formattedChanges.join(", ");
    } else {
      return `${formattedChanges.slice(0, 2).join(", ")} +${
        formattedChanges.length - 2
      } more`;
    }
  }

  if (entityType === "invoice" || entityType === "bill") {
    const lines = formatInvoiceBillChangeSummaryLines(
      entityType === "invoice" ? "invoice" : "bill",
      changeDetails,
      taxes,
    );
    if (lines.length === 0) {
      if (changeDetails.reason?.toLowerCase().includes("manual")) {
        return "Manual adjustment: Revenue reversal";
      }
      if (changeDetails.reason?.toLowerCase().includes("variance")) {
        return changeDetails.reason;
      }
      return null;
    }
    if (lines.length <= 2) {
      return lines.join(", ");
    }
    return `${lines.slice(0, 2).join(", ")} +${lines.length - 2} more`;
  }

  // If no field_changes, check for reason/manual adjustments
  if (
    !changeDetails.field_changes ||
    changeDetails.field_changes.length === 0
  ) {
    if (changeDetails.reason?.toLowerCase().includes("manual")) {
      return "Manual adjustment: Revenue reversal";
    }
    if (changeDetails.reason?.toLowerCase().includes("variance")) {
      return changeDetails.reason;
    }
    return null;
  }

  // Route to module-specific formatter
  let formattedChanges: string[] = [];

  switch (entityType) {
    case "credit_note":
    case "debit_note":
      formattedChanges = formatCreditDebitNoteChanges(
        changeDetails.field_changes,
      );
      break;
    case "journal":
    case "ap_ar_journal":
      formattedChanges = formatJournalChanges(
        changeDetails.field_changes,
        changeDetails.raw_json,
        changeDetails.original_json,
        changeDetails.action_types,
      );
      break;
    case "bank_transaction":
      formattedChanges = formatBankTransactionChanges(changeDetails);
      break;
    case "bank_account":
      formattedChanges = formatBankAccountChangesForCard(
        changeDetails,
        actionCode,
      );
      break;
    case "gl": {
      const glRaw = changeDetails.raw_json as any;
      const glActionTypes = changeDetails.action_types || [];
      const fmtGlType = (val: string) =>
        String(val)
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

      if (glActionTypes.includes("delete")) {
        return `Delete GL: ${glRaw?.account_name || glRaw?.account_id || "Unknown"}`;
      }
      if (glActionTypes.includes("create")) {
        const parts: string[] = [];
        if (glRaw?.account_name) parts.push(`Name: ${glRaw.account_name}`);
        if (glRaw?.account_type)
          parts.push(`Type: ${fmtGlType(String(glRaw.account_type))}`);
        const usedIn: string[] = [];
        if (glRaw?.invoice) usedIn.push("Invoices");
        if (glRaw?.bill) usedIn.push("Bills");
        if (usedIn.length > 0) parts.push(`Use in: ${usedIn.join(" & ")}`);
        return parts.length > 0 ? parts.join(" · ") : null;
      }
      // update – show friendly field_changes
      if (
        changeDetails.field_changes &&
        changeDetails.field_changes.length > 0
      ) {
        const glFieldLabels: Record<string, string> = {
          account_name: "Name",
          account_type: "Type",
          parent_account_id: "Parent",
          invoice: "Invoices",
          bill: "Bills",
        };
        formattedChanges = changeDetails.field_changes
          .slice(0, 3)
          .map((fc: any) => {
            const label = glFieldLabels[fc.field] ?? fc.field;
            let oldVal = fc.old;
            let newVal = fc.new;
            if (fc.field === "account_type") {
              oldVal = fmtGlType(oldVal);
              newVal = fmtGlType(newVal);
            }
            if (fc.field === "invoice" || fc.field === "bill") {
              oldVal = oldVal === "true" ? "Yes" : "No";
              newVal = newVal === "true" ? "Yes" : "No";
            }
            return `${label}: ${oldVal} → ${newVal}`;
          });
      }
      break;
    }
    default:
      // Fallback: look for GL changes
      const glChange = changeDetails.field_changes.find(
        (fc: any) =>
          fc.field.toLowerCase().includes("gl") ||
          fc.field.toLowerCase().includes("account"),
      );
      if (glChange) {
        return `New GL used: ${glChange.new}`;
      }
      return null;
  }

  // Format the changes for display (show first 2, or all if less than 2)
  if (formattedChanges.length === 0) {
    return null;
  }

  if (formattedChanges.length <= 2) {
    return formattedChanges.join(", ");
  } else {
    return `${formattedChanges.slice(0, 2).join(", ")} +${
      formattedChanges.length - 2
    } more`;
  }
};

const getValidationStatus = (
  rawJson: any,
  entityType: string,
): {
  icon: any;
  text: string;
  color: string;
} | null => {
  // This is a placeholder - you may need to add actual validation logic
  if (entityType === "invoice" || entityType === "bill") {
    return {
      icon: FeatherCheck,
      text: "Bank reconciled",
      color: "text-success-600",
    };
  }
  return null;
};

export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  approval,
  onReview,
  id,
}) => {
  const { taxes } = useTaxes();
  // ZOUR-65 F4: names for the GST registration a journal is filed under.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();

  /** "Bengaluru Branch · 29AAECN3779L1ZQ". NULL resolves to the primary. */
  const describeLocation = (locationId: string | null): string | null => {
    const match =
      activeLocations.find(
        (candidate) => candidate.zoho_location_id === locationId,
      ) ?? (locationId ? null : primaryLocation);

    return match
      ? `${match.name}${match.gstin ? ` \u00b7 ${match.gstin}` : ""}`
      : null;
  };

  const rawJson = approval.changeDetails?.raw_json;
  const actionTypes = approval.changeDetails?.action_types || [];
  const isGlBulkUpload =
    approval.entityType === "gl" &&
    (actionTypes.includes("bulk_upload") ||
      (approval.actionCode ?? "").toLowerCase() === "bulk_upload");
  const changeMessage = getChangeMessage(
    approval.changeDetails,
    approval.entityType,
    approval.actionCode,
    taxes,
  );
  const validationStatus = getValidationStatus(rawJson, approval.entityType);
  const AlertIcon = getAlertIcon(actionTypes);

  return (
    <div
      id={id || `approval-${approval.id}`}
      className={`flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background shadow-sm ${getBorderColor(
        approval.status,
        actionTypes,
      )}`}
    >
      <div className="flex w-full items-start justify-between gap-4 px-6 pt-5">
        <div className="flex flex-col items-start gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap w-full">
            {approval.entityType === "month_end_checklist_item" ? (
              <>
                <span className="text-body font-body text-subtext-color">
                  Month End Checklist
                </span>
                <span className="text-body font-body text-subtext-color">
                  →
                </span>
                <span className="text-body font-body text-default-font">
                  {approval.accountingArea || "N/A"}
                </span>
              </>
            ) : approval.entityType === "bank_transaction" ? (
              <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                Bank Transaction - {approval.actionName}
              </span>
            ) : approval.entityType === "bank_account" ? (
              <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                {getBankAccountApprovalTitle(approval.actionCode, rawJson)}
                {rawJson?.account_name
                  ? ` – ${String(rawJson.account_name)}`
                  : ""}
              </span>
            ) : (
              <span className="text-body-bold font-body-bold text-default-font">
                {(() => {
                  const entityName =
                    ENTITY_TYPE_DISPLAY[approval.entityType] ||
                    approval.entityType;

                  // Get reference number
                  let refNumber: string | null = null;
                  if (
                    approval.entityType === "journal" ||
                    approval.entityType === "ap_ar_journal"
                  ) {
                    const originalJson = approval.changeDetails?.original_json;
                    const journalDetails = getJournalDetailsForCard(
                      approval.changeDetails,
                    );
                    refNumber =
                      originalJson?.journal_number ||
                      journalDetails.referenceNumber ||
                      originalJson?.reference_number ||
                      rawJson?.journal_number ||
                      rawJson?.reference_number ||
                      null;
                  } else if (approval.entityType === "journal_template") {
                    // For journal templates, show template name
                    refNumber = rawJson?.template_name || null;
                  } else if (
                    (approval.entityType === "customer" ||
                      approval.entityType === "vendor" ||
                      approval.entityType === "gl") &&
                    (approval.actionCode ?? "").toLowerCase() === "bulk_upload"
                  ) {
                    const rows = rawJson?.rows;
                    const n = Array.isArray(rows) ? rows.length : 0;
                    refNumber =
                      n > 0 ? `${n} row${n === 1 ? "" : "s"}` : "Bulk upload";
                  } else if (
                    approval.entityType !== "bank_transaction" &&
                    approval.entityType !== "month_end_checklist_item"
                  ) {
                    const refSourceJson =
                      approval.entityType === "invoice" ||
                      approval.entityType === "bill"
                        ? approval.changeDetails?.raw_json ?? rawJson
                        : rawJson;
                    const ref = getReferenceNumber(
                      refSourceJson,
                      approval.entityType,
                    );
                    refNumber = ref && ref !== "N/A" ? ref : null;
                  }

                  // Format action types
                  const actionTypesFormatted = formatActionTypes(actionTypes);

                  // Build the header string
                  let header = entityName;
                  if (refNumber) {
                    header += ` - ${refNumber}`;
                  }
                  // Omit parenthetical when it would duplicate refNumber (e.g. "Journal - Create Recurring (Create Recurring)" -> "Journal - Create Recurring")
                  if (
                    actionTypesFormatted &&
                    actionTypesFormatted !== refNumber
                  ) {
                    header += ` (${actionTypesFormatted})`;
                  }

                  return header;
                })()}
              </span>
            )}
            {/* <Badge
              variant={getStatusBadgeVariant(approval.status, actionTypes)}
            >
              {getStatusBadgeText(actionTypes)}
            </Badge> */}
          </div>
          {/* Request comments – same styled box for all entity types (label + comment on one line) */}
          {approval.requestComments && (
            <div className="flex flex-row flex-nowrap items-center gap-2 rounded-md bg-info-50 px-3 py-2 border border-solid border-info-200 w-full">
              <span className="text-caption font-caption text-info-700 font-semibold shrink-0">
                Accountant's Comments:
              </span>
              <span className="text-body font-body text-info-800 min-w-0">
                {approval.requestComments}
              </span>
            </div>
          )}
          {approval.entityType === "invoice" ||
          approval.entityType === "bill" ? (
            <InvoiceBillApprovalCardSummary
              kind={
                approval.entityType === "invoice" ? "invoice" : "bill"
              }
              changeDetails={approval.changeDetails}
            />
          ) : approval.entityType === "bank_transaction" ? (
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Transaction Date:
                </span>
                <span className="text-body font-body text-default-font">
                  {formatDateFull(
                    approval.changeDetails?.raw_json?.date ||
                      approval.changeDetails?.raw_json?.transaction_date ||
                      (approval.changeDetails?.raw_json?.categorize_in_zoho_json
                        ?.date
                        ? approval.changeDetails.raw_json
                            .categorize_in_zoho_json.date
                        : undefined),
                  ) || "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Transaction Amount:
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {approval.changeDetails?.raw_json?.amount ||
                  approval.changeDetails?.raw_json?.transaction_amount ||
                  approval.changeDetails?.raw_json?.categorize_in_zoho_json
                    ?.amount
                    ? formatAmount(
                        approval.changeDetails.raw_json,
                        approval.entityType,
                        approval.changeDetails,
                      )
                    : "N/A"}
                </span>
              </div>
            </div>
          ) : approval.entityType === "bank_account" && rawJson ? (
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Account type:
                </span>
                <span className="text-body font-body text-default-font">
                  {rawJson.account_type === "credit_card"
                    ? "Credit card"
                    : "Bank account"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Account name:
                </span>
                <span className="text-body font-body text-default-font">
                  {String(rawJson.account_name ?? "—")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Account number:
                </span>
                <span className="text-body font-body text-default-font">
                  {String(rawJson.account_number ?? "—")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Currency:
                </span>
                <span className="text-body font-body text-default-font">
                  {String(rawJson.currency_code ?? rawJson.currency_id ?? "—")}
                </span>
              </div>
            </div>
          ) : approval.entityType === "gl" && rawJson && !isGlBulkUpload ? (
            <div className="flex flex-col items-start gap-2">
              {(approval.changeDetails?.action_types || []).includes(
                "delete",
              ) ? (
                <>
                  <span className="text-body font-body text-error-600">
                    Delete GL:{" "}
                    {String(rawJson.account_name ?? rawJson.account_id ?? "—")}
                  </span>
                  {rawJson.account_type && (
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Type:
                      </span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.account_type)
                          .split("_")
                          .map(
                            (w: string) =>
                              w.charAt(0).toUpperCase() + w.slice(1),
                          )
                          .join(" ")}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Account name:
                    </span>
                    <span className="text-body font-body text-default-font">
                      {String(rawJson.account_name ?? "—")}
                    </span>
                  </div>
                  {rawJson.account_type && (
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Account type:
                      </span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.account_type)
                          .split("_")
                          .map(
                            (w: string) =>
                              w.charAt(0).toUpperCase() + w.slice(1),
                          )
                          .join(" ")}
                      </span>
                    </div>
                  )}
                  {rawJson.parent_account_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Parent account:
                      </span>
                      <span className="text-body font-body text-default-font">
                        {String(rawJson.parent_account_name)}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const usedIn: string[] = [];
                    if (rawJson.invoice) usedIn.push("Invoices");
                    if (rawJson.bill) usedIn.push("Bills");
                    return usedIn.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-body font-body text-subtext-color">
                          Use in:
                        </span>
                        <span className="text-body font-body text-default-font">
                          {usedIn.join(" & ")}
                        </span>
                      </div>
                    ) : null;
                  })()}
                </>
              )}
            </div>
          ) : approval.entityType === "month_end_checklist_item" ? (
            <div className="flex flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                {approval.itemTitle || "Checklist Item"}
              </span>
              {approval.itemDescription && (
                <span className="text-body font-body text-default-font">
                  {approval.itemDescription}
                </span>
              )}
              {(() => {
                const requestText =
                  approval.actionCode === "to_be_done_later"
                    ? "Request: Mark this checklist item as To be done later."
                    : approval.actionCode === "mark_as_done" ||
                        approval.actionCode === "marked_to_done" ||
                        approval.actionCode === "accountant_checklist"
                      ? "Request: Mark this checklist item as done."
                      : null;

                return requestText ? (
                  <span className="text-body font-body text-info-700">
                    {requestText}
                  </span>
                ) : null;
              })()}
              {approval.attachments && approval.attachments.length > 0 && (
                <span className="text-body font-body text-subtext-color">
                  {approval.attachments.length} attachment
                  {approval.attachments.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
          ) : approval.entityType === "journal" ||
            approval.entityType === "ap_ar_journal" ? (
            <div className="flex flex-col items-start gap-2">
              {(() => {
                const journalDetails = getJournalDetailsForCard(
                  approval.changeDetails,
                );
                const locationLabel =
                  hasMultipleLocations &&
                  approval.entityType === "journal"
                    ? describeLocation(journalDetails.locationId)
                    : null;
                return (
                  <>
                    {/* 2. Notes */}
                    {journalDetails.notes && (
                      <div className="flex items-center gap-2">
                        <span className="text-body font-body text-subtext-color">
                          Journal Notes -
                        </span>
                        <span className="text-body font-body text-default-font">
                          {journalDetails.notes}
                        </span>
                      </div>
                    )}
                    {/* ZOUR-65 F4: which GST registration this journal is filed
                        under. An AP/AR journal is excluded — it takes its
                        location from the document it was raised for. */}
                    {locationLabel && (
                      <div className="flex items-center gap-2">
                        <span className="text-body font-body text-subtext-color">
                          Location -
                        </span>
                        <span className="text-body font-body text-default-font">
                          {locationLabel}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : approval.entityType === "journal_template" ? (
            <div className="flex flex-col items-start gap-2">
              {(() => {
                const actionTypes = approval.changeDetails?.action_types || [];
                const hasCancelAction = actionTypes.includes(
                  "cancel_recurring_before_end",
                );

                // For cancel action, show notes instead
                if (hasCancelAction) {
                  const rawJson = approval.changeDetails?.raw_json;
                  const notes = rawJson?.notes || "No notes available";
                  return (
                    <span className="text-body font-body text-subtext-color">
                      {notes}
                    </span>
                  );
                }

                // For other actions, show detailed info
                const rawJson = approval.changeDetails?.raw_json;
                const frequency = rawJson?.recurrence_frequency || "N/A";
                const startDate = rawJson?.starts_on
                  ? formatDateFull(rawJson.starts_on)
                  : "N/A";
                const endDate = rawJson?.ends_on
                  ? formatDateFull(rawJson.ends_on)
                  : rawJson?.never_expires
                    ? "Never expires"
                    : "N/A";
                const totalGenerated = rawJson?.total_generated_count || 0;

                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Frequency:
                      </span>
                      <span className="text-body font-body text-default-font">
                        {frequency}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Period:
                      </span>
                      <span className="text-body font-body text-default-font">
                        {startDate} → {endDate}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Total Generated:
                      </span>
                      <span className="text-body font-body text-default-font">
                        {totalGenerated} journal
                        {totalGenerated !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : approval.entityType === "tenant_compliance_calendar" ? (
            <div className="flex flex-col items-start gap-2">
              {(() => {
                const rawJson = approval.changeDetails?.raw_json;
                const complianceFormName =
                  rawJson?.compliance_form_name || "N/A";
                const period = rawJson?.period || "N/A";
                const dueDate = rawJson?.due_date
                  ? formatDateFull(rawJson.due_date)
                  : "N/A";
                const periodStartDate = rawJson?.period_start_date
                  ? formatDateFull(rawJson.period_start_date)
                  : null;
                const periodEndDate = rawJson?.period_end_date
                  ? formatDateFull(rawJson.period_end_date)
                  : null;
                const typeDetails = rawJson?.compliance_type_details || {};
                const authority = typeDetails.authority || null;
                const frequency = typeDetails.frequency || null;
                const complianceName = typeDetails.compliance_name || null;
                const complianceType = typeDetails.compliance_type || null;

                return (
                  <>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {complianceFormName}
                    </span>
                    <div className="flex flex-col items-start gap-1">
                      {period && period !== "N/A" && (
                        <span className="text-body font-body text-subtext-color">
                          Period: {period}
                        </span>
                      )}
                      {periodStartDate && periodEndDate && (
                        <span className="text-body font-body text-subtext-color">
                          {periodStartDate} - {periodEndDate}
                        </span>
                      )}
                      {dueDate && dueDate !== "N/A" && (
                        <span className="text-body font-body text-subtext-color">
                          Due Date: {dueDate}
                        </span>
                      )}
                      {authority && (
                        <span className="text-body font-body text-subtext-color">
                          Authority: {authority}
                        </span>
                      )}
                      {frequency && (
                        <span className="text-body font-body text-subtext-color">
                          Frequency: {frequency}
                        </span>
                      )}
                      {complianceName && (
                        <span className="text-body font-body text-subtext-color">
                          {complianceName}
                        </span>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : approval.entityType === "tenant_compliance_calendar" ? (
            <div className="flex flex-col items-start gap-2">
              {(() => {
                const rawJson = approval.changeDetails?.raw_json;
                const complianceFormName =
                  rawJson?.compliance_form_name || "N/A";
                const period = rawJson?.period || "N/A";
                const dueDate = rawJson?.due_date
                  ? formatDateFull(rawJson.due_date)
                  : "N/A";
                const periodStartDate = rawJson?.period_start_date
                  ? formatDateFull(rawJson.period_start_date)
                  : null;
                const periodEndDate = rawJson?.period_end_date
                  ? formatDateFull(rawJson.period_end_date)
                  : null;
                const typeDetails = rawJson?.compliance_type_details || {};
                const authority = typeDetails.authority || null;
                const frequency = typeDetails.frequency || null;
                const complianceName = typeDetails.compliance_name || null;
                const complianceType = typeDetails.compliance_type || null;

                return (
                  <>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {complianceFormName}
                    </span>
                    <div className="flex flex-col items-start gap-1">
                      {period && period !== "N/A" && (
                        <span className="text-body font-body text-subtext-color">
                          Period: {period}
                        </span>
                      )}
                      {periodStartDate && periodEndDate && (
                        <span className="text-body font-body text-subtext-color">
                          {periodStartDate} - {periodEndDate}
                        </span>
                      )}
                      {dueDate && dueDate !== "N/A" && (
                        <span className="text-body font-body text-subtext-color">
                          Due Date: {dueDate}
                        </span>
                      )}
                      {authority && (
                        <span className="text-body font-body text-subtext-color">
                          Authority: {authority}
                        </span>
                      )}
                      {frequency && (
                        <span className="text-body font-body text-subtext-color">
                          Frequency: {frequency}
                        </span>
                      )}
                      {complianceName && (
                        <span className="text-body font-body text-subtext-color">
                          {complianceName}
                        </span>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : null}
          {(approval.changeDetails?.closed_month === true ||
            actionTypes?.includes("month_close")) && (
            <div className="flex items-center gap-2 rounded-md bg-neutral-100 px-2 py-1 border border-solid border-neutral-200 w-fit">
              <span className="text-caption font-caption text-neutral-700">
                Post-close change
              </span>
            </div>
          )}
          {changeMessage &&
            approval.entityType !== "invoice" &&
            approval.entityType !== "bill" && (
              <div className="flex items-start gap-2">
                <AlertIcon
                  className={`mt-0.5 shrink-0 text-body font-body ${
                    actionTypes?.includes("update") ||
                    actionTypes?.includes("revised")
                      ? "text-error-600"
                      : actionTypes?.includes("auto_flagged")
                        ? "text-brand-600"
                        : "text-warning-600"
                  }`}
                />
                <span className="text-body font-body text-neutral-600 min-w-0">
                  {changeMessage}
                </span>
              </div>
            )}
        </div>
        <Button variant="brand-primary" size="small" onClick={onReview}>
          Review
        </Button>
      </div>
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      <div className="flex w-full items-center justify-between px-6 pb-5">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <FeatherClock className="text-body font-body text-neutral-400" />
            <span className="text-caption font-caption text-subtext-color">
              {approval.status === "approved" || approval.status === "rejected"
                ? `Modified ${formatDateTimeInIST(
                    approval.reviewedAt ||
                      approval.updatedAt ||
                      approval.createdAt,
                  )}`
                : `Raised ${formatDateTimeInIST(approval.createdAt)}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Avatar variant="brand" size="x-small" image="">
              {approval.requesterName
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase() || "U"}
            </Avatar>
            <span className="text-caption font-caption text-subtext-color">
              {approval.requesterName || "Unknown"}
            </span>
          </div>
          {approval.reviewerName && (
            <div className="flex items-center gap-2">
              <span className="text-caption font-caption text-subtext-color">
                Raised to:
              </span>
              <span className="text-caption font-caption text-default-font">
                {approval.reviewerName}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
